import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  QrCode,
  Upload,
  Trash2,
  X,
  ChevronLeft,
  Album,
  Images,
  ScanFace,
} from "lucide-react";

const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

/* =========================================================
   ADMIN EVENTS
========================================================= */

export default function AdminEvents() {
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    title: "",
    booking_id: "",
    match_threshold: 0.52,
  });

  /* =======================================================
     LOAD EVENTS
  ======================================================= */

  const load = async () => {
    setLoading(true);

    try {
      const [e, b] = await Promise.all([
        api.get("/events"),
        api.get("/bookings"),
      ]);

      setEvents(e.data);
      setBookings(b.data);
    } catch (err) {
      console.error("Failed to load events:", err);
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =======================================================
     CREATE EVENT
  ======================================================= */

  const createEvent = async (e) => {
    e.preventDefault();

    if (!form.title) {
      return toast.error("Title required");
    }

    setCreating(true);

    try {
      await api.post("/events", form);

      toast.success("Event created");

      setForm({
        title: "",
        booking_id: "",
        match_threshold: 0.52,
      });

      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  /* =======================================================
     DELETE EVENT
  ======================================================= */

  const removeEvent = async (id) => {
    if (!window.confirm("Delete event, all photos & selections?")) {
      return;
    }

    try {
      await api.delete(`/events/${id}`);

      toast.success("Deleted");

      if (selectedEvent?.id === id) {
        setSelectedEvent(null);
      }

      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  /* =======================================================
     EVENT DETAIL
  ======================================================= */

  if (selectedEvent) {
    return (
      <EventDetail
        event={selectedEvent}
        bookings={bookings}
        onBack={() => {
          setSelectedEvent(null);
          load();
        }}
      />
    );
  }

  /* =======================================================
     EVENT LIST
  ======================================================= */

  return (
    <div data-testid="admin-events" className="p-10">
      <div className="mb-8">
        <div className="overline mb-2">AI Photo Delivery</div>

        <h1 className="font-serif-display text-4xl">
          Events
        </h1>

        <p className="text-neutral-500 text-sm mt-2 max-w-xl">
          Upload event photos, generate a QR for the client,
          and let them find their photos with a selfie.
        </p>
      </div>

      {/* ===================================================
          CREATE EVENT
      =================================================== */}

      <form
        onSubmit={createEvent}
        data-testid="event-create-form"
        className="bg-[#0a0a0c] border border-white/10 p-6 rounded-sm mb-10 grid md:grid-cols-4 gap-6 items-end"
      >
        <div className="md:col-span-2">
          <label className="overline block mb-2">
            Event title
          </label>

          <input
            data-testid="event-title"
            value={form.title}
            onChange={(e) =>
              setForm({
                ...form,
                title: e.target.value,
              })
            }
            className="line-input w-full py-3"
            placeholder="Rahul & Priya wedding"
          />
        </div>

        <div>
          <label className="overline block mb-2">
            Link booking (optional)
          </label>

          <select
            data-testid="event-booking"
            value={form.booking_id}
            onChange={(e) =>
              setForm({
                ...form,
                booking_id: e.target.value,
              })
            }
            className="line-input w-full py-3"
          >
            <option
              value=""
              className="bg-[#0a0a0c]"
            >
              None
            </option>

            {bookings.map((b) => (
              <option
                key={b.id}
                value={b.id}
                className="bg-[#0a0a0c]"
              >
                {b.client_name} — {b.event_date}
              </option>
            ))}
          </select>
        </div>

        <button
          type="submit"
          disabled={creating}
          data-testid="event-create-btn"
          className="bg-white text-black py-3 text-sm uppercase tracking-[0.2em] disabled:opacity-50"
        >
          {creating ? "Creating…" : "Create event"}
        </button>
      </form>

      {/* ===================================================
          EVENTS TABLE
      =================================================== */}

      <div className="bg-[#0a0a0c] border border-white/10 rounded-sm overflow-hidden">
        <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 overline text-neutral-500">
          <div className="col-span-4">Event</div>
          <div className="col-span-3">Client</div>
          <div className="col-span-2">Photos</div>
          <div className="col-span-2">Created</div>
          <div className="col-span-1"></div>
        </div>

        {loading ? (
          <div className="p-6 text-neutral-500 text-sm">
            Loading…
          </div>
        ) : events.length === 0 ? (
          <div className="p-6 text-neutral-500 text-sm">
            No events yet. Create one to start delivering photos.
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {events.map((ev) => (
              <div
                key={ev.id}
                className="grid grid-cols-12 gap-4 px-6 py-5 items-center hover:bg-white/5"
              >
                <button
                  data-testid={`event-open-${ev.id}`}
                  onClick={() => setSelectedEvent(ev)}
                  className="col-span-4 text-left text-white font-serif-display text-lg"
                >
                  {ev.title}
                </button>

                <div className="col-span-3 text-sm text-neutral-300">
                  {ev.client_name || "—"}

                  {ev.client_email && (
                    <div className="text-xs text-neutral-500">
                      {ev.client_email}
                    </div>
                  )}
                </div>

                <div className="col-span-2 text-sm text-neutral-400">
                  {ev.photo_count || 0} photo
                  {ev.photo_count === 1 ? "" : "s"}
                </div>

                <div className="col-span-2 text-xs text-neutral-500">
                  {ev.created_at?.slice(0, 10)}
                </div>

                <div className="col-span-1 text-right">
                  <button
                    data-testid={`event-delete-${ev.id}`}
                    onClick={() => removeEvent(ev.id)}
                    className="text-xs uppercase text-red-300 hover:text-red-200"
                    title="Delete event"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* =========================================================
   EVENT DETAIL
========================================================= */

function EventDetail({ event, bookings, onBack }) {

  /*
   * IMPORTANT:
   *
   * We no longer load every photo here.
   *
   * Only summary numbers are loaded.
   */

  const [photoSummary, setPhotoSummary] = useState({
    photo_count: event.photo_count || 0,
    face_count: 0,
    pending_count: 0,
    failed_count: 0,
  });

  const [selections, setSelections] = useState([]);

  const [uploading, setUploading] = useState(false);

  const [uploadCount, setUploadCount] = useState(null);
  const [driveFolder, setDriveFolder] = useState(event.drive_folder_url || "");
  const [driveConnecting, setDriveConnecting] = useState(false);

  const [qr, setQr] = useState(null);
  const [qrMode, setQrMode] = useState("guest");
  const [selectedPhotos, setSelectedPhotos] = useState(null);
  const [selectedPhotosLoading, setSelectedPhotosLoading] = useState(false);

  const [qrBooking, setQrBooking] = useState(
    event.booking_id || ""
  );

  const [summaryLoading, setSummaryLoading] = useState(true);


  /* =======================================================
     LOAD EVENT SUMMARY

     NO PHOTO IMAGE URLs ARE LOADED HERE.
  ======================================================= */

  const load = async () => {
    try {
      setSummaryLoading(true);

      const [summaryResponse, selectionResponse] =
        await Promise.all([
          api.get(
            `/events/${event.id}/photo-summary`
          ),

          api.get(
            `/events/${event.id}/album-selections`
          ),
        ]);

      console.log(
        "Photo summary:",
        summaryResponse.data
      );

      console.log(
        "Album selections:",
        selectionResponse.data
      );

      setPhotoSummary(summaryResponse.data);

      setSelections(selectionResponse.data);
    } catch (err) {
      console.error(
        "Failed to load event detail:",
        err
      );

      toast.error(formatApiError(err));
    } finally {
      setSummaryLoading(false);
    }
  };


  useEffect(() => {
    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id]);


  /* =======================================================
     UPLOAD PHOTOS

     Browser -> R2 -> Register with backend
  ======================================================= */

  const uploadPhotos = async (e) => {
    const files = Array.from(e.target.files || []);

    if (files.length === 0) return;

    setUploading(true);
    setUploadCount({ done: 0, total: files.length });

    let uploaded = 0;
    let failed = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > MAX_UPLOAD_BYTES) {
        toast.error(`${file.name} exceeds 50MB — skipped`);
        failed += 1;
        setUploadCount({ done: i + 1, total: files.length });
        continue;
      }

      try {
        // Use the backend R2 upload path. This is the same server-side
        // storage approach used by website Gallery/Portfolio uploads and
        // avoids browser-to-R2 presigned PUT failures.
        const fd = new FormData();
        fd.append("file", file);

        await api.post(
          `/events/${event.id}/photos`,
          fd,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        uploaded += 1;
      } catch (err) {
        failed += 1;
        console.error("Upload error:", err);
        toast.error(
          `${file.name}: ${formatApiError(err) || "Upload failed"}`
        );
      }

      setUploadCount({ done: i + 1, total: files.length });
    }

    if (uploaded > 0) {
      toast.success(
        failed > 0
          ? `${uploaded} photo(s) uploaded, ${failed} failed`
          : `${uploaded} photo(s) uploaded successfully`
      );
    } else if (failed > 0) {
      toast.error("No photos were uploaded");
    }

    setUploading(false);
    setUploadCount(null);
    e.target.value = "";

    await load();
  };

  const connectDriveFolder = async () => {
    if (!driveFolder.trim()) {
      return toast.error("Paste a Google Drive folder link");
    }

    try {
      setDriveConnecting(true);
      const { data } = await api.post(
        `/events/${event.id}/drive-folder`,
        { folder_url: driveFolder.trim() }
      );

      toast.success(
        `Drive connected: ${data.added} new photo(s), ${data.skipped} already indexed`
      );

      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDriveConnecting(false);
    }
  };

  const syncDriveFolder = async () => {
    try {
      setDriveConnecting(true);
      const { data } = await api.post(
        `/events/${event.id}/drive-sync`
      );

      toast.success(
        `Drive sync complete: ${data.added} new photo(s), ${data.skipped} already indexed`
      );

      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDriveConnecting(false);
    }
  };


  /* =======================================================
     GENERATE CLIENT QR
  ======================================================= */

  const generateQr = async (mode = "guest") => {

    if (!qrBooking) {
      return toast.error(
        "Pick a booking (client) first"
      );
    }


    try {

      const fd = new FormData();

      fd.append(
        "booking_id",
        qrBooking
      );
      fd.append("access_mode", mode);


      const { data } =
        await api.post(
          `/events/${event.id}/qr`,
          fd
        );


      setQr(data);
      setQrMode(mode);

    } catch (err) {

      toast.error(
        formatApiError(err)
      );
    }
  };


  /* =======================================================
     UI
  ======================================================= */

  return (
    <div
      data-testid="event-detail"
      className="p-10"
    >

      {/* ===================================================
          BACK
      =================================================== */}

      <button
        data-testid="event-back"
        onClick={onBack}
        className="text-xs uppercase tracking-widest text-neutral-400 hover:text-white flex items-center gap-2 mb-6"
      >
        <ChevronLeft size={14} />

        Back to events
      </button>


      {/* ===================================================
          EVENT HEADER
      =================================================== */}

      <div className="flex justify-between items-end flex-wrap gap-6 mb-8">

        <div>

          <div className="overline mb-2">
            Event
          </div>

          <h1 className="font-serif-display text-4xl">
            {event.title}
          </h1>


          {summaryLoading ? (

            <div className="text-sm text-neutral-500 mt-2">
              Loading event summary…
            </div>

          ) : (

            <div className="text-sm text-neutral-400 mt-2">

              {photoSummary.photo_count || 0}{" "}
              photo
              {photoSummary.photo_count === 1
                ? ""
                : "s"}

              {" · "}

              {photoSummary.face_count || 0}{" "}
              face
              {photoSummary.face_count === 1
                ? ""
                : "s"}{" "}
              indexed

            </div>
          )}

        </div>


        <div className="w-full md:w-auto min-w-[320px]">
          <div className="overline text-neutral-400 mb-2">
            Google Drive Event Folder
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <input
              value={driveFolder}
              onChange={(e) => setDriveFolder(e.target.value)}
              placeholder="Paste Google Drive folder link"
              className="min-w-0 flex-1 bg-[#0a0a0c] border border-white/15 px-3 py-3 text-sm text-white"
            />

            <button
              type="button"
              onClick={connectDriveFolder}
              disabled={driveConnecting}
              className="bg-white text-black px-4 py-3 text-xs uppercase tracking-[0.16em] disabled:opacity-50"
            >
              {driveConnecting ? "Working…" : "Connect & Index"}
            </button>

            <button
              type="button"
              onClick={syncDriveFolder}
              disabled={driveConnecting}
              className="border border-white/20 px-4 py-3 text-xs uppercase tracking-[0.16em] disabled:opacity-50"
            >
              Sync
            </button>
          </div>

          <div className="text-[11px] text-neutral-500 mt-2">
            Share the folder with the Wedding Touch service account as Viewer.
          </div>
        </div>

      </div>


      {/* ===================================================
          PHOTO STATUS

          Counts only.
          No images loaded.
      =================================================== */}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10">

        <div className="bg-[#0a0a0c] border border-white/10 p-5">

          <div className="flex items-center gap-2 text-neutral-500 mb-2">
            <Images size={14} />

            <span className="overline">
              Photos Uploaded
            </span>
          </div>

          <div className="font-serif-display text-3xl">
            {photoSummary.photo_count || 0}
          </div>

        </div>


        <div className="bg-[#0a0a0c] border border-white/10 p-5">

          <div className="flex items-center gap-2 text-neutral-500 mb-2">

            <ScanFace size={14} />

            <span className="overline">
              Faces Indexed
            </span>

          </div>

          <div className="font-serif-display text-3xl">
            {photoSummary.face_count || 0}
          </div>

        </div>


        <div className="bg-[#0a0a0c] border border-white/10 p-5">

          <div className="overline text-neutral-500 mb-2">
            Processing
          </div>

          <div className="font-serif-display text-3xl">
            {photoSummary.pending_count || 0}
          </div>

        </div>


        <div className="bg-[#0a0a0c] border border-white/10 p-5">

          <div className="overline text-neutral-500 mb-2">
            Failed
          </div>

          <div
            className={`font-serif-display text-3xl ${
              photoSummary.failed_count > 0
                ? "text-red-400"
                : ""
            }`}
          >
            {photoSummary.failed_count || 0}
          </div>

        </div>

      </div>


      {/* ===================================================
          QR GENERATOR
      =================================================== */}

      <div className="bg-[#0a0a0c] border border-white/10 p-6 rounded-sm mb-10">

        <div className="overline mb-4 text-neutral-500 flex items-center gap-2">

          <QrCode size={14} />

          Client QR (auto-login)

        </div>


        <div className="grid md:grid-cols-3 gap-6 items-end">

          <div className="md:col-span-2">

            <label className="overline block mb-2">
              Select booking / client
            </label>


            <select
              data-testid="qr-booking-select"
              value={qrBooking}
              onChange={(e) =>
                setQrBooking(
                  e.target.value
                )
              }
              className="line-input w-full py-3"
            >

              <option
                value=""
                className="bg-[#0a0a0c]"
              >
                Pick a booking
              </option>


              {bookings.map((b) => (

                <option
                  key={b.id}
                  value={b.id}
                  className="bg-[#0a0a0c]"
                >
                  {b.client_name} —{" "}
                  {b.client_email}
                </option>

              ))}

            </select>

          </div>


          <div className="grid grid-cols-2 gap-2">
            <button
              data-testid="qr-generate"
              onClick={() => generateQr("guest")}
              className="bg-white text-black py-3 px-3 text-xs uppercase tracking-[0.15em]"
            >
              Guest QR
            </button>
            <button
              data-testid="album-qr-generate"
              onClick={() => generateQr("album")}
              className="bg-[#E11D2A] text-white py-3 px-3 text-xs uppercase tracking-[0.15em]"
            >
              Album QR
            </button>
          </div>

        </div>


        {/* =================================================
            QR RESULT
        ================================================= */}

        {qr && (

          <div className="mt-6 grid md:grid-cols-3 gap-6 items-center">

            <img
              data-testid="qr-image"
              src={qr.qr}
              alt="QR"
              className="w-56 bg-white p-3"
            />


            <div className="md:col-span-2 space-y-3">

              <div>

                <div className="overline text-neutral-500">
                  {qrMode === "album" ? "Album Selection QR" : "Guest QR"}
                </div>

                <div className="text-white text-lg">
                  {qr.client_name}
                </div>

              </div>


              <div>

                <div className="overline text-neutral-500">
                  Share URL
                </div>

                <div className="text-sm text-neutral-300 break-all">
                  {qr.url}
                </div>

              </div>


              <div className="flex gap-3">

                <a
                  href={qr.qr}
                  download={`qr-${
                    qr.client_name ||
                    "client"
                  }.png`}
                  className="text-xs uppercase tracking-widest border border-white/20 px-4 py-2 hover:bg-white/5"
                >
                  Download PNG
                </a>


                <button
                  data-testid="qr-copy"
                  onClick={() => {

                    navigator.clipboard.writeText(
                      qr.url
                    );

                    toast.success(
                      "Link copied"
                    );
                  }}
                  className="text-xs uppercase tracking-widest border border-white/20 px-4 py-2 hover:bg-white/5"
                >
                  Copy link
                </button>


                <button
                  data-testid="qr-close"
                  onClick={() =>
                    setQr(null)
                  }
                  className="text-neutral-500 hover:text-white ml-auto"
                >
                  <X size={16} />
                </button>

              </div>

            </div>

          </div>

        )}

      </div>


      {/* ===================================================
          ALBUM SELECTIONS

          This still loads selection metadata only.
          It does NOT load all event images.
      =================================================== */}

      {selections.length > 0 && (

        <div className="bg-[#0a0a0c] border border-white/10 p-6 rounded-sm mb-10">

          <div className="overline mb-4 text-neutral-500 flex items-center gap-2">

            <Album size={14} />

            Album selections from clients

          </div>


          <div className="space-y-3">

            {selections.map((s) => (

              <div
                key={s.id}
                data-testid={`selection-${s.id}`}
                className="flex justify-between items-center border border-white/5 p-3 rounded-sm"
              >

                <div>

                  <div className="text-white">

                    {s.client_name ||
                      "Client"}{" "}

                    <span className="text-neutral-500 text-xs">
                      ({s.client_email})
                    </span>

                  </div>


                  <div className="text-xs text-neutral-500">

                    Updated{" "}

                    {s.updated_at
                      ?.slice(0, 16)
                      .replace(
                        "T",
                        " "
                      )}

                  </div>

                </div>


                <div className="flex items-center gap-4">
                  <button
                    type="button"
                    onClick={async () => {
                      setSelectedPhotosLoading(true);
                      try {
                        const { data } = await api.get(`/events/${event.id}/album-selections/${s.id}/photos`);
                        setSelectedPhotos({ selection: s, photos: data });
                      } catch (err) {
                        toast.error(formatApiError(err));
                      } finally {
                        setSelectedPhotosLoading(false);
                      }
                    }}
                    className="text-xs uppercase tracking-widest border border-white/20 px-4 py-2 hover:bg-white/5"
                  >
                    {selectedPhotosLoading ? "Loading..." : "View selected"}
                  </button>
                  <div className="text-2xl font-serif-display text-[#E11D2A]">
                    {s.photo_ids?.length || 0}
                    <span className="text-xs text-neutral-500 ml-2">selected</span>
                  </div>
                </div>

              </div>

            ))}

          </div>

        </div>

      )}


      {selectedPhotos && (
        <div className="fixed inset-0 z-[100] bg-black/85 p-4 md:p-10 overflow-y-auto">
          <div className="max-w-7xl mx-auto bg-[#0a0a0c] border border-white/10 p-5 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="overline text-neutral-500">Album selection</div>
                <h3 className="font-serif-display text-3xl">{selectedPhotos.selection.client_name || "Client"}</h3>
              </div>
              <button onClick={() => setSelectedPhotos(null)} className="p-3 border border-white/20"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {selectedPhotos.photos.map((p) => (
                <a key={p.id} href={p.image_url} target="_blank" rel="noreferrer" className="block border border-white/10 bg-black">
                  <img src={p.image_url} alt={p.filename || "Selected photo"} className="w-full aspect-square object-cover" loading="lazy" />
                  <div className="p-2 text-xs text-neutral-400 truncate">{p.filename}</div>
                </a>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===================================================
          IMPORTANT:
          OLD PHOTO GRID REMOVED

          We intentionally do NOT render event photos here.
      =================================================== */}

    </div>
  );
}